import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';

import { ModalService, NotificationService, OkModalComponent, PrintService, ShipperResponse } from 'tms-lib';
import {
  quickShippingDetailsBook,
  ShippingDetailsQuickModalComponent,
} from '../../components/shipping/shipping-details-quick-modal/shipping-details-quick-modal.component';
import { ShippingPacksModalComponent } from '../../components/shipping/shipping-packs-modal/shipping-packs-modal.component';
import { QuickDespatchBookResult, QuickDespatchOutcome, QuickDespatchPack } from '../../models';
import { ShipperInterfaceHeaderService } from '../edit-create/sales/shipper-interface-header.service';
import { QuickDespatchService } from './quick-despatch.service';

/**
 * Acts on the quick despatch outcomes (QuickDespatchAndBookShipping) a cut or pick returns - the ONE
 * place every cutting and picking screen hands its `quickDespatch` list to.
 *
 * The API sends codes; the wording is here. Outcomes are taken one after another, so only one
 * Shipping Packs / Shipping Details window is ever open at a time. A screen whose setting is off
 * gets an empty list, and nothing happens.
 *
 * - NotFullyPicked / OnStop / CreditNotPassed / NotCreated: a warning, no despatch note.
 * - Created + NoApi: the server has created and printed the note - say so.
 * - Created + Packs: ask for the packs' weights; Save books the shipping, Back prints unbooked.
 * - Created + Details: show the shipping details to correct; Save books, Back prints unbooked.
 */
@Injectable({
  providedIn: 'root',
})
export class QuickDespatchHandlerService {
  private readonly quickDespatchService = inject(QuickDespatchService);
  private readonly shipperInterfaceHeaderService = inject(ShipperInterfaceHeaderService);
  private readonly modalService = inject(ModalService);
  private readonly notificationService = inject(NotificationService);
  private readonly printService = inject(PrintService);

  // ONE queue for the whole app, so two cuts or picks in quick succession still open their windows
  // one after the other. A root service lives as long as the app, so the subscription is never torn
  // down; each step completes (HTTP calls, modal results), so the queue always moves on.
  private readonly queue = new Subject<QuickDespatchOutcome>();

  constructor() {
    this.queue
      .pipe(concatMap((outcome: QuickDespatchOutcome) => this.handleOutcome(outcome).pipe(catchError(() => EMPTY))))
      .subscribe();
  }

  handle(outcomes: QuickDespatchOutcome[] | undefined): void {
    (outcomes ?? []).forEach((outcome: QuickDespatchOutcome) => this.queue.next(outcome));
  }

  private handleOutcome(outcome: QuickDespatchOutcome): Observable<unknown> {
    switch (outcome.code) {
      case 'NotFullyPicked':
        this.notificationService.warning(
          $localize`Order ${outcome.orderNo} is not fully picked - the despatch note has not been created.`,
        );
        return EMPTY;
      case 'OnStop':
        this.notificationService.warning(
          $localize`Despatch note not created for order ${outcome.orderNo} - the customer is on stop.`,
        );
        return EMPTY;
      case 'CreditNotPassed':
        this.notificationService.warning(
          $localize`Despatch note not created for order ${outcome.orderNo} - it has not been credit passed.`,
        );
        return EMPTY;
      case 'NotCreated':
        this.notificationService.warning(
          $localize`Despatch note not created for order ${outcome.orderNo} - there was nothing left to put on it.`,
        );
        return EMPTY;
      case 'Failed':
        this.notificationService.warning(
          $localize`Despatch note not created for order ${outcome.orderNo} - something went wrong. Create it from Sales Despatch Notes.`,
        );
        return EMPTY;
      case 'Created':
        return this.created(outcome);
      default:
        return EMPTY;
    }
  }

  private created(outcome: QuickDespatchOutcome): Observable<unknown> {
    switch (outcome.shipping) {
      case 'Packs':
        return this.askForPacks(outcome);
      case 'Details':
        return this.askForDetails(outcome);
      default:
        // NoApi: the server has created and printed the note already.
        this.notificationService.success(
          $localize`Despatch note ${outcome.despatchNoteNo} created for order ${outcome.orderNo}.`,
        );
        this.warnNotPrinted(outcome.notPrinted);
        return EMPTY;
    }
  }

  private askForPacks(outcome: QuickDespatchOutcome): Observable<unknown> {
    const { modal, component } = this.modalService.openMedium<ShippingPacksModalComponent>(ShippingPacksModalComponent);
    component.outcome.set(outcome);

    // Save closes WITH the packs; anything else (Back, a dismiss) is a cancel.
    return this.modalResult(modal.result).pipe(
      switchMap((result: unknown) =>
        Array.isArray(result) ? this.book(outcome, result as QuickDespatchPack[]) : this.print(outcome),
      ),
    );
  }

  private askForDetails(outcome: QuickDespatchOutcome): Observable<unknown> {
    const { modal, component } = this.modalService.openLarge<ShippingDetailsQuickModalComponent>(
      ShippingDetailsQuickModalComponent,
    );
    component.outcome.set(outcome);

    // Closes with quickShippingDetailsBook only once the details pass the checks. Back closes
    // without it (or dismisses), even after a save made inside the window - that is a cancel.
    return this.modalResult(modal.result).pipe(
      switchMap((result: unknown) => (result === quickShippingDetailsBook ? this.book(outcome) : this.print(outcome))),
    );
  }

  /** A modal's result as an observable that always emits once - a dismissal emits undefined. */
  private modalResult(result: Promise<unknown>): Observable<unknown> {
    return from(result.catch(() => undefined));
  }

  private book(outcome: QuickDespatchOutcome, packs?: QuickDespatchPack[]): Observable<unknown> {
    return this.quickDespatchService.book(outcome.despatchNoteId, packs).pipe(
      switchMap((result: QuickDespatchBookResult) => this.bookResult(outcome, result)),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.apiFail(
          error,
          $localize`The shipping could not be booked for despatch note ${outcome.despatchNoteNo}.`,
        );
        // The note must still print (D-4): the server did not get as far as printing it.
        return this.print(outcome);
      }),
    );
  }

  private bookResult(outcome: QuickDespatchOutcome, result: QuickDespatchBookResult): Observable<unknown> {
    const despatchNoteNo = result?.despatchNoteNo ?? outcome.despatchNoteNo;

    if (result?.booked) {
      this.notificationService.success(
        $localize`Shipping booked for despatch note ${despatchNoteNo} - tracking number ${result.trackingNumber}.`,
      );
      // Booked, but with something the user should know. Already translated by the API.
      (result.cautions ?? []).forEach((caution: string) => this.notificationService.warning(caution));
      this.warnNotPrinted(result.notPrinted);
      return this.printLabel(
        result.despatchNoteId ?? outcome.despatchNoteId,
        result.shipperInterfaceHeaderId ?? outcome.shipperInterfaceHeaderId,
      );
    }

    this.warnNotPrinted(result?.notPrinted);
    return this.showOk(
      $localize`Booking Failed` +
        ` - ${result?.errorMessage}. ` +
        $localize`Despatch note ${despatchNoteNo} has been created - book the shipping from Sales Despatch Notes.`,
    );
  }

  // The carrier label, exactly as the shipper interface's Reprint Label prints it.
  private printLabel(despatchNoteId: string, shipperInterfaceHeaderId: string): Observable<unknown> {
    return this.shipperInterfaceHeaderService.reprintLabel(despatchNoteId, shipperInterfaceHeaderId).pipe(
      switchMap((response: ShipperResponse) => {
        if (response?.trackingNumber) {
          return this.printService.printShippingLabels(response);
        }
        return this.showOk($localize`Booking Failed` + ` - ${response?.errorMessage}`);
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.apiFail(error, $localize`The carrier label could not be printed.`);
        return EMPTY;
      }),
    );
  }

  private print(outcome: QuickDespatchOutcome): Observable<unknown> {
    const notBooked = $localize`Shipping not booked for despatch note ${outcome.despatchNoteNo} - book it from Sales Despatch Notes.`;

    return this.quickDespatchService.print(outcome.despatchNoteId).pipe(
      map((result: QuickDespatchBookResult) => {
        this.warnNotPrinted(result?.notPrinted);
        this.notificationService.warning(notBooked);
        return result;
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.apiFail(
          error,
          $localize`Despatch note ${outcome.despatchNoteNo} could not be printed.`,
        );
        this.notificationService.warning(notBooked);
        return of(undefined);
      }),
    );
  }

  /** An Ok box headed Book Shipping; the chain moves on once it is closed. */
  private showOk(message: string): Observable<unknown> {
    const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
    component.header.set($localize`Book Shipping`);
    component.message.set(message);
    return this.modalResult(modal.result);
  }

  // Already-translated "could not print" lines from the API.
  private warnNotPrinted(lines: string[] | undefined) {
    (lines ?? []).forEach((line: string) => this.notificationService.warning(line));
  }
}
